import { sendCalls, waitForCallsStatus, waitForTransactionReceipt, WriteContractParameters } from '@wagmi/core';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Address, encodeFunctionData } from 'viem';
import { useConnection, useSendTransaction, useWriteContract } from 'wagmi';

import { SequentialStepView } from '@/app/components/Modal/SequentialStepModal';
import { StepStatus } from '@/app/components/Modal/StepIcon';
import { config } from '@/app/providers/app/wagmiConfig';
import { WYLDS_ALLOWANCE_INSUFFICIENT_MESS } from '@/app/shared/constants/errors';
import {
  handleTransactionError,
  isUserRejectedRequestError,
} from '@/app/shared/hooks/blockchain/useSendTransactions/handleTransactionError';
import { TransactionStatus } from '@/app/shared/hooks/blockchain/useSendTransactions/types';

export type SequentialSendTx = {
  to: Address;
  data?: `0x${string}`;
  value?: bigint;
  gas?: bigint;
  gasPrice?: bigint;
};

export type SequentialStepPlan = {
  id: string;
  title: string;
  description: string;
  sectionId?: string;
  tx?: WriteContractParameters;
  resolveTx?: () => Promise<WriteContractParameters>;
  sendTx?: SequentialSendTx;
  resolveSendTx?: () => Promise<SequentialSendTx>;
  isComplete?: boolean;
};

export type AtomicBatchGroup = {
  stepIds: string[];
  resolveCalls: (pendingStepIds: string[]) => Promise<WriteContractParameters[]>;
};

interface UseSendSequentialStepsArgs {
  steps: SequentialStepPlan[];
  planKey?: string;
  enabled?: boolean;
  stepByStep?: boolean;
  atomicBatches?: AtomicBatchGroup[];
  onStepSuccess?: (stepId: string, hash: Address) => void | Promise<void>;
}

const toStatusMap = (steps: SequentialStepPlan[]): Record<string, StepStatus> =>
  Object.fromEntries(
    steps.map((step) => [
      step.id,
      step.isComplete ? StepStatus.Success : StepStatus.Pending,
    ]),
  );

const encodeWriteContractCall = (param: WriteContractParameters) => ({
  to: param.address as Address,
  data: encodeFunctionData({
    abi: param.abi,
    functionName: param.functionName,
    args: param.args ?? [],
  }),
  value: param.value,
});

export const useSendSequentialSteps = ({
  steps,
  planKey = '',
  enabled = true,
  stepByStep = false,
  atomicBatches,
  onStepSuccess,
}: UseSendSequentialStepsArgs) => {
  const { isConnected, address: account } = useConnection();

  const [errorMessage, setErrorMessage] = useState('');
  const [isUserRejectedError, setIsUserRejectedError] = useState(false);
  const [txHash, setTxHash] = useState<Address | undefined>();
  const [statuses, setStatuses] = useState<Record<string, StepStatus>>(() => toStatusMap(steps));
  const [isRunning, setIsRunning] = useState(false);
  const [isComplete, setIsComplete] = useState(false);

  const { mutateAsync: writeContract, reset: resetWriteContract } = useWriteContract();
  const { mutateAsync: sendTransaction, reset: resetSendTransaction } = useSendTransaction();

  const stepsRef = useRef(steps);
  stepsRef.current = steps;

  const statusesRef = useRef(statuses);
  statusesRef.current = statuses;

  const onStepSuccessRef = useRef(onStepSuccess);
  onStepSuccessRef.current = onStepSuccess;

  const atomicBatchesRef = useRef(atomicBatches);
  atomicBatchesRef.current = atomicBatches;

  const isRunningRef = useRef(false);

  const completionKey = useMemo(
    () => steps.map((step) => `${step.id}:${step.isComplete ? 1 : 0}`).join('|'),
    [steps],
  );

  useEffect(() => {
    const next = toStatusMap(stepsRef.current);
    setStatuses(next);
    statusesRef.current = next;
    setIsComplete(false);
    setErrorMessage('');
    setIsUserRejectedError(false);
    setTxHash(undefined);
  }, [planKey]);

  useEffect(() => {
    setStatuses((prev) => {
      const next = { ...prev };
      let changed = false;

      for (const step of stepsRef.current) {
        if (step.isComplete && next[step.id] !== StepStatus.Success) {
          next[step.id] = StepStatus.Success;
          changed = true;
        }

        if (next[step.id] === undefined) {
          next[step.id] = step.isComplete ? StepStatus.Success : StepStatus.Pending;
          changed = true;
        }
      }

      if (!changed) return prev;

      statusesRef.current = next;
      return next;
    });
  }, [completionKey]);

  const resetTransactionState = useCallback(() => {
    resetWriteContract();
    resetSendTransaction();
    setErrorMessage('');
    setIsUserRejectedError(false);
    setTxHash(undefined);
    setIsRunning(false);
    isRunningRef.current = false;
    setIsComplete(false);

    const next = toStatusMap(stepsRef.current);
    setStatuses(next);
    statusesRef.current = next;
  }, [resetSendTransaction, resetWriteContract]);

  const handleSend = useCallback(async () => {
    const currentSteps = stepsRef.current;

    if (!enabled || currentSteps.length === 0 || !isConnected || !account) return;
    if (isRunningRef.current) return;

    isRunningRef.current = true;
    setIsRunning(true);
    setIsComplete(false);
    setErrorMessage('');
    setIsUserRejectedError(false);

    let localStatuses = { ...statusesRef.current };

    for (const step of currentSteps) {
      if (localStatuses[step.id] === StepStatus.Error) {
        localStatuses[step.id] = StepStatus.Pending;
      }

      if (step.isComplete) {
        localStatuses[step.id] = StepStatus.Success;
      }
    }

    setStatuses(localStatuses);
    statusesRef.current = localStatuses;

    let currentId: string | undefined;
    let batchPendingIds: string[] = [];
    let usedBatching = false;

    try {
      let lastHash: Address | undefined;

      for (const planned of currentSteps) {
        const step = stepsRef.current.find((item) => item.id === planned.id) ?? planned;

        if (localStatuses[step.id] === StepStatus.Success || step.isComplete) {
          if (localStatuses[step.id] !== StepStatus.Success) {
            localStatuses = { ...localStatuses, [step.id]: StepStatus.Success };
            setStatuses(localStatuses);
            statusesRef.current = localStatuses;
          }
          continue;
        }

        const batchGroup = atomicBatchesRef.current?.find((group) => group.stepIds.includes(step.id));

        if (batchGroup) {
          usedBatching = true;
          batchPendingIds = batchGroup.stepIds.filter((stepId) => {
            const plannedStep = stepsRef.current.find((item) => item.id === stepId);
            return (
              localStatuses[stepId] !== StepStatus.Success && !(plannedStep?.isComplete ?? false)
            );
          });

          if (batchPendingIds.length === 0) continue;

          currentId = batchPendingIds[0];
          for (const stepId of batchPendingIds) {
            localStatuses = { ...localStatuses, [stepId]: StepStatus.AwaitingWallet };
          }
          setStatuses(localStatuses);
          statusesRef.current = localStatuses;

          const writeParams = await batchGroup.resolveCalls(batchPendingIds);
          if (writeParams.length === 0) {
            throw new Error('Nothing to submit in batch');
          }

          const { id: callsId } = await sendCalls(config, {
            calls: writeParams.map(encodeWriteContractCall),
          });

          for (const stepId of batchPendingIds) {
            localStatuses = { ...localStatuses, [stepId]: StepStatus.Loading };
          }
          setStatuses(localStatuses);
          statusesRef.current = localStatuses;

          const callsStatus = await waitForCallsStatus(config, { id: callsId });
          if (callsStatus.status === 'failure') {
            throw new Error('Batch transaction failed');
          }

          const receipts = callsStatus.receipts ?? [];
          const failed = receipts.find((receipt) => receipt.status === 'reverted');
          if (failed) {
            throw new Error('Batch transaction reverted');
          }

          lastHash =
            receipts.length > 0
              ? (receipts[receipts.length - 1]?.transactionHash as Address | undefined)
              : undefined;
          if (lastHash) setTxHash(lastHash);

          for (const stepId of batchPendingIds) {
            localStatuses = { ...localStatuses, [stepId]: StepStatus.Success };
          }
          setStatuses(localStatuses);
          statusesRef.current = localStatuses;

          for (const stepId of batchPendingIds) {
            await onStepSuccessRef.current?.(stepId, lastHash ?? ('0x' as Address));
          }

          if (stepByStep) break;
          continue;
        }

        currentId = step.id;
        localStatuses = { ...localStatuses, [step.id]: StepStatus.AwaitingWallet };
        setStatuses(localStatuses);
        statusesRef.current = localStatuses;

        const usesSend = !!step.sendTx || !!step.resolveSendTx;

        if (usesSend) {
          const sendTx = step.resolveSendTx ? await step.resolveSendTx() : step.sendTx!;

          lastHash = await sendTransaction({
            to: sendTx.to,
            data: sendTx.data,
            value: sendTx.value ?? 0n,
            ...(sendTx.gas ? { gas: sendTx.gas } : {}),
            ...(sendTx.gasPrice ? { gasPrice: sendTx.gasPrice } : {}),
          });
        } else {
          const tx = step.resolveTx ? await step.resolveTx() : step.tx!;
          lastHash = await writeContract(tx);
        }

        setTxHash(lastHash);

        localStatuses = { ...localStatuses, [step.id]: StepStatus.Loading };
        setStatuses(localStatuses);
        statusesRef.current = localStatuses;

        const receipt = await waitForTransactionReceipt(config, { hash: lastHash });
        if (receipt.status === 'reverted') {
          throw new Error('Transaction reverted');
        }

        localStatuses = { ...localStatuses, [step.id]: StepStatus.Success };
        setStatuses(localStatuses);
        statusesRef.current = localStatuses;

        await onStepSuccessRef.current?.(step.id, lastHash);

        if (stepByStep) {
          break;
        }
      }

      const allDone = stepsRef.current.every(
        (step) => localStatuses[step.id] === StepStatus.Success || step.isComplete,
      );
      if (allDone) {
        setIsComplete(true);
      }
    } catch (error) {
      console.error(error);

      const userRejected = isUserRejectedRequestError(error);
      const parsedErrorMessage = handleTransactionError({
        error,
        action: usedBatching ? 'batch_send' : 'send_transaction',
        isBatching: usedBatching,
        account,
      });

      const errorIds =
        batchPendingIds.length > 0 ? [...batchPendingIds] : currentId ? [currentId] : [];

      if (
        !userRejected &&
        currentId === 'mint-mzusd' &&
        (parsedErrorMessage === WYLDS_ALLOWANCE_INSUFFICIENT_MESS ||
          (error instanceof Error && error.message === WYLDS_ALLOWANCE_INSUFFICIENT_MESS))
      ) {
        errorIds.unshift('approve-wylds');
      }

      if (errorIds.length > 0) {
        setStatuses((prev) => {
          const next = { ...prev };
          for (const stepId of errorIds) {
            next[stepId] = StepStatus.Error;
          }
          statusesRef.current = next;
          return next;
        });
      }

      setIsUserRejectedError(userRejected);
      setErrorMessage(parsedErrorMessage);
      setIsComplete(false);
    } finally {
      isRunningRef.current = false;
      setIsRunning(false);
    }
  }, [account, enabled, isConnected, sendTransaction, stepByStep, writeContract]);

  const stepViews: SequentialStepView[] = steps.map((step) => ({
    id: step.id,
    title: step.title,
    description: step.description,
    sectionId: step.sectionId,
    status: statuses[step.id] ?? (step.isComplete ? StepStatus.Success : StepStatus.Pending),
  }));

  const transactionStatusData: TransactionStatus = {
    isLoading: isRunning,
    isError: !!errorMessage && !isComplete,
    isSuccess: isComplete,
  };

  return {
    steps: stepViews,
    handleSend,
    handleRetry: handleSend,
    resetTransactionState,
    transactionStatusData,
    errorMessage,
    isUserRejectedError,
    txHash,
    isRunning,
    isComplete,
  };
};
